import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  Tooltip,
  Checkbox,
  Badge,
} from '@mui/material';
import {
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiX,
  FiEdit2,
  FiMessageSquare,
  FiTrash2,
} from 'react-icons/fi';
import { Sort, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { FaRupeeSign } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { remindersAPI } from '../services/api';
import TableLoader from '../components/TableLoader';
import { colors } from '../theme/modernTheme';
import { showSuccess, showError } from '../utils/toast';
import { formatDateForDisplay } from '../utils/dateUtils';
import { useMultiSelect } from '../hooks/useMultiSelect';
import BulkActionToolbar from '../components/BulkActionToolbar';
import BulkDeleteDialog from '../components/BulkDeleteDialog';
import CancelReminderDialog from '../components/CancelReminderDialog';
import CommentModal from '../components/CommentModal';
import { useLoading } from '../context/LoadingContext';
import { useAuth } from '../context/AuthContext';
import { adjustPageAfterDeletion } from '../utils/paginationUtils';
import { getFrequencyLabel } from '../utils/frequencyLabel';

export default function Reminders() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);

  const canDelete = user?.role === 'ADMIN';

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting
  const [orderBy, setOrderBy] = useState('reminder_date');
  const [order, setOrder] = useState('asc');

  // Multi-select state
  const multiSelect = useMultiSelect(reminders, totalCount, 'id');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Cancel reminder dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reminderToCancel, setReminderToCancel] = useState(null);

  // Comment modal state
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);

  // Temporary filter states (not applied until user clicks Apply)
  const [searchInput, setSearchInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [monthInput, setMonthInput] = useState('');

  // Applied filter states (used for API calls)
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [month, setMonth] = useState('');
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const monthNum = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${monthNum}`;
  };

  // Initialize filters from URL parameters BEFORE first load
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const monthParam = searchParams.get('month');
    const currentMonth = getCurrentMonth();

    if (statusParam) {
      setStatusInput(statusParam);
      setStatusFilter(statusParam);
    }

    // Only set default month if month parameter is not in URL
    // If month= (empty string), it means clear the filter
    if (monthParam !== null) {
      // month parameter exists in URL (could be empty string or a value)
      setMonthInput(monthParam);
      setMonth(monthParam);
    } else {
      // No month parameter in URL, set default to current month
      setMonthInput(currentMonth);
      setMonth(currentMonth);
    }

    // Mark filters as initialized
    setFiltersInitialized(true);
  }, [searchParams]);

  useEffect(() => {
    // Only load reminders after filters are initialized
    if (filtersInitialized) {
      loadReminders();
    }
  }, [page, statusFilter, month, search, orderBy, order, filtersInitialized]);

  const loadReminders = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
      };

      if (statusFilter) params.status = statusFilter;
      if (month) params.month = month;
      if (search) params.search = search;

      const response = await remindersAPI.getAll(params);
      setReminders(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setSearch(searchInput);
    setStatusFilter(statusInput);
    setMonth(monthInput);
    setPage(0);
  };

  const handleClearFilters = () => {
    const currentMonth = getCurrentMonth();
    setSearchInput('');
    setStatusInput('');
    setMonthInput(currentMonth);
    setSearch('');
    setStatusFilter('');
    setMonth(currentMonth);
    setPage(0);
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      // If already sorting by this column, toggle order
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      // First click on a new column
      if (column === 'reminder_date') {
        // Reminder date defaults to ascending
        setOrder('asc');
      } else {
        // Other columns default to descending
        setOrder('desc');
      }
      setOrderBy(column);
    }
    setPage(0); // Reset to first page when sorting changes
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const hasActiveFilters =
    searchInput || statusInput || (monthInput && monthInput !== getCurrentMonth());

  // Check if filters have changed from applied state
  const hasFilterChanges =
    searchInput !== search || statusInput !== statusFilter || monthInput !== month;

  const handleCancelClick = (reminder) => {
    setReminderToCancel(reminder);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async (reminderId, pausePledge) => {
    try {
      await remindersAPI.cancel(reminderId, pausePledge);
      multiSelect.removeFromSelection(reminderId);
      setCancelDialogOpen(false);
      setReminderToCancel(null);

      // Adjust page if needed after deletion
      const newTotalCount = totalCount - 1;
      adjustPageAfterDeletion(page, newTotalCount, rowsPerPage, setPage);

      loadReminders();
      showSuccess(
        pausePledge ? 'Reminder cancelled and pledge paused' : 'Reminder cancelled successfully',
      );
    } catch (error) {
      showError(error.error?.message || 'Failed to cancel reminder');
      throw error; // Re-throw to let dialog handle loading state
    }
  };

  const handleCommentClick = (reminder) => {
    setSelectedReminder(reminder);
    setCommentModalOpen(true);
  };

  const handleCommentCountChange = (reminderId, delta) => {
    setReminders((prevReminders) =>
      prevReminders.map((reminder) =>
        reminder.id === reminderId
          ? { ...reminder, comment_count: Math.max(0, (reminder.comment_count || 0) + delta) }
          : reminder,
      ),
    );
  };

  const getContactName = (reminder) => {
    if (reminder.first_name) {
      return `${reminder.first_name} ${reminder.last_name || ''}`.trim();
    }
    return reminder.organization_name || 'Unknown Contact';
  };

  const handleReminderClick = (reminder) => {
    navigate(`/reminders/${reminder.id}`);
  };

  const handleBulkDelete = async () => {
    try {
      showLoading();
      const selectionData = multiSelect.getSelectionData();
      const deleteCount = multiSelect.selectedCount;

      // Validate that items are selected
      if (!selectionData.selectAll && (!selectionData.ids || selectionData.ids.length === 0)) {
        showError('No reminders selected');
        hideLoading();
        return;
      }

      // Build filters object from current filter state
      const filters = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (month) filters.month = month;

      await remindersAPI.bulkDelete({
        ...selectionData,
        filters,
      });

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();

      // Adjust page if needed after bulk deletion
      const newTotalCount = totalCount - deleteCount;
      adjustPageAfterDeletion(page, newTotalCount, rowsPerPage, setPage);

      loadReminders();
      showSuccess(`Successfully deleted ${deleteCount} reminder(s)`);
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete reminders');
    } finally {
      hideLoading();
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2} mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Reminders
        </Typography>
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '4px',
          boxShadow: 'none',
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search reminders..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                      setPage(0);
                    }}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: '4px' }}
                  >
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch style={{ marginRight: 8, color: colors.text.secondary }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.background.paper,
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    '& .MuiMenu-list': {
                      padding: '0px',
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                All Status
              </MenuItem>
              <MenuItem
                value="PENDING"
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                Pending
              </MenuItem>
              <MenuItem
                value="COMPLETED"
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                Completed
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              type="month"
              label="Month"
              value={monthInput}
              onChange={(e) => setMonthInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                onClick: (e) => e.target.showPicker?.(),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                disableRipple
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  mr: -1,
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Apply
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <IconButton
                size="small"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPage(Math.min(Math.ceil(totalCount / rowsPerPage) - 1, page + 1))}
                disabled={
                  page >= Math.ceil(totalCount / rowsPerPage) - 1 || multiSelect.selectedCount > 0
                }
                sx={{
                  borderRadius: 1,
                  marginLeft: -2,
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={canDelete}
        itemName="reminders"
      />

      {loading ? (
        <TableLoader message="Loading reminders..." />
      ) : reminders.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: colors.text.secondary,
          }}
        >
          <Typography variant="body1">No reminders found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: `1px solid ${colors.border.main}`,
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <Table>
            <TableHead sx={{ borderBottom: '1px solid #E5E7EB' }}>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '2%', py: 1, px: 1 }}>
                  <Checkbox
                    size="medium"
                    indeterminate={multiSelect.isSomeCurrentPageSelected}
                    checked={multiSelect.isAllCurrentPageSelected}
                    onChange={multiSelect.toggleSelectAll}
                    sx={{
                      padding: '4px',
                      color: colors.text.secondary,
                      '& .MuiSvgIcon-root': { fontSize: 28 },
                      transform: 'scale(1.2)',
                      '&.Mui-checked': {
                        color: colors.primary.main,
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: colors.primary.main,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: colors.action.hover,
                    },
                  }}
                  onClick={() => handleSort('reminder_date')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Reminder Date
                    {orderBy === 'reminder_date' ? (
                      order === 'asc' ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" sx={{ opacity: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Donor
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: colors.action.hover,
                    },
                  }}
                  onClick={() => handleSort('pledge_amount')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Pledge Amount
                    {orderBy === 'pledge_amount' ? (
                      order === 'asc' ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" sx={{ opacity: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Collected Amount
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Frequency
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'center',
                  }}
                >
                  {multiSelect.selectedCount > 0 ? '' : 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((reminder) => {
                const isSelected = multiSelect.isSelected(reminder);
                return (
                  <TableRow
                    key={reminder.id}
                    selected={isSelected}
                    onClick={() => handleReminderClick(reminder)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: colors.action.row },
                      '&:not(:last-of-type)': {
                        borderBottom: `1px solid ${colors.border.main}`,
                      },
                      ...(isSelected && {
                        backgroundColor: `${colors.primary.main}08`,
                        '&:hover': {
                          backgroundColor: `${colors.primary.main}12`,
                        },
                      }),
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{ py: 1.5, px: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        size="medium"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          multiSelect.toggleSelect(reminder);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{
                          padding: '4px',
                          '& .MuiSvgIcon-root': { fontSize: 28 },
                          transform: 'scale(1.2)',
                          color: colors.text.secondary,
                          '&.Mui-checked': {
                            color: colors.primary.main,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '120px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1,
                      }}
                    >
                      {reminder.reminder_date ? formatDateForDisplay(reminder.reminder_date) : '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: '180px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                        }}
                      >
                        <Tooltip title={getContactName(reminder)} arrow placement="top">
                          <span>{getContactName(reminder)}</span>
                        </Tooltip>
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '120px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1,
                      }}
                    >
                      {reminder.pledge_amount
                        ? `₹${parseInt(reminder.pledge_amount).toLocaleString()}`
                        : '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '120px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1,
                      }}
                    >
                      {reminder.collected_amount
                        ? `₹${parseInt(reminder.collected_amount).toLocaleString()}`
                        : '₹0'}
                    </TableCell>
                    <TableCell sx={{ width: '120px', py: 1 }}>
                      <Chip
                        label={reminder.status}
                        size="small"
                        sx={{
                          backgroundColor:
                            reminder.status === 'PENDING'
                              ? `${colors.info.main}15`
                              : reminder.status === 'COMPLETED'
                                ? `${colors.success.main}15`
                                : colors.action.hover,
                          color:
                            reminder.status === 'PENDING'
                              ? colors.info.main
                              : reminder.status === 'COMPLETED'
                                ? colors.success.main
                                : colors.text.secondary,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          borderRadius: '4px',
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '120px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1,
                      }}
                    >
                      {getFrequencyLabel(reminder.frequency) || '-'}
                    </TableCell>
                    <TableCell sx={{ width: '160px', py: 1 }}>
                      {multiSelect.selectedCount > 0 ? (
                        <Box sx={{ width: '156px' }} />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                          }}
                        >
                          {reminder.status === 'PENDING' && (
                            <Tooltip title="Add Transaction" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/transactions/new?reminder_id=${reminder.id}`);
                                }}
                                sx={{
                                  color: colors.text.primary,
                                  p: 1,
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: colors.success.main,
                                  },
                                }}
                              >
                                <FaRupeeSign size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Comments" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCommentClick(reminder);
                              }}
                              sx={{
                                color: colors.text.primary,
                                p: 1,
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: colors.primary.main,
                                },
                              }}
                            >
                              <Badge
                                badgeContent={reminder.comment_count || 0}
                                color="primary"
                                max={99}
                                sx={{
                                  '& .MuiBadge-badge': {
                                    fontSize: '0.7rem !important',
                                    height: '14px',
                                    minWidth: '14px',
                                    padding: '0 2px',
                                    right: -4,
                                    top: -2,
                                  },
                                }}
                              >
                                <FiMessageSquare size={16} />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                          {reminder.status === 'PENDING' && (
                            <Tooltip title="Delete Reminder" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelClick(reminder);
                                }}
                                sx={{
                                  color: colors.text.primary,
                                  p: 1,
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: colors.error.main,
                                  },
                                }}
                              >
                                <FiTrash2 size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && reminders.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1,
            opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
            pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
          }}
        >
          <IconButton
            size="small"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0 || multiSelect.selectedCount > 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage(Math.min(Math.ceil(totalCount / rowsPerPage) - 1, page + 1))}
            disabled={
              page >= Math.ceil(totalCount / rowsPerPage) - 1 || multiSelect.selectedCount > 0
            }
            sx={{
              marginLeft: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Cancel Reminder Dialog */}
      <CancelReminderDialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setReminderToCancel(null);
        }}
        onConfirm={handleCancelConfirm}
        reminder={reminderToCancel}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="reminders"
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: 'center' }}
        >
          Deleting this reminders will NOT cancel the pledges. The pledges will remain active, and
          future reminders will still be generated.
        </Typography>
      </BulkDeleteDialog>

      {/* Comment Modal */}
      <CommentModal
        open={commentModalOpen}
        onClose={() => {
          setCommentModalOpen(false);
          setSelectedReminder(null);
        }}
        contactId={selectedReminder?.contact_id}
        contactName={selectedReminder ? getContactName(selectedReminder) : ''}
        reminderId={selectedReminder?.id}
        onCommentChange={handleCommentCountChange}
      />
    </Box>
  );
}
